import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { enqueueSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { useBlocker } from 'react-router';
import { useVirtualizer } from '@tanstack/react-virtual';
import { AlertTriangleIcon, LockIcon, MoreVerticalIcon, PlusIcon, SaveIcon } from 'lucide-react';
import {
  Button,
  Checkbox,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Input,
} from '@monksflow/monks-ui';
import { FormDialog } from '@/shared/ui/FormDialog';
import { CopyButton } from '@/shared/ui';
import { EmptyState } from '@/shared/ui/EmptyState';
import { DeleteConfirmationModal } from '@/shared/ui/DeleteConfirmationModal';
import {
  assembleRecord,
  assembleTrackingCode,
  orderedOutputs,
  DEFAULT_TRACKING_CODE_CAP,
  MAX_RECORDS_PER_MINT,
} from '@inference.engine/taxonomy/web';
import type {
  TaxonomyField,
  TaxonomyFieldValue,
  TaxonomyOutput,
  TaxonomyRule,
  TaxonomySettings,
} from '@/entities/taxonomy';
import {
  useAppendRecords,
  useClearRecords,
  useDeleteRecords,
  useGenerateRecordCodes,
  type TaxonomyRecord,
  type TaxonomyRecordCells,
  type TaxonomyRecordOutputs,
} from '@/entities/taxonomy-dataset';
import type { ResolvedList } from '@/entities/taxonomy-list';
import {
  clampToLength,
  MAX_CELL_VALUE_LENGTH,
  TAXONOMY_FIELD_TYPE_LABELS,
} from '@/entities/taxonomy';
import { at, get } from '../lib/lookup';
import { matchVocabulary } from '../lib/matchVocabulary';
import { gridColumns } from '../lib/columns';
import type { GridColumn } from '../lib/columns';
import { applyRules, usableOptions, vocabularySource } from '../lib/applyRules';
import { rowDefects, type CellDefect } from '../lib/completeness';
import { useEffectiveFields, type EffectiveFieldsResolver } from '../lib/useEffectiveFields';
import { useSaveGate } from '../lib/useSaveGate';
import { optionLabel } from '../lib/comboboxChoices';
import { useRecordDraft } from '../lib/useRecordDraft';
import type { CellWrite } from '../lib/useRecordDraft';
import { RecordCell } from './RecordCell';
import { BulkToolbar } from './BulkToolbar';

/**
 * Fixed row height, so the virtualizer needs no measurement pass.
 *
 * `h-10` on the cell — see `RecordCell`. Two `box-sizing: border-box` declarations
 * apply (preflight, plus an unlayered `*` rule in `styles/global.css`), so that
 * 40px ALREADY includes the bottom border: measured `offsetHeight 40` against
 * `clientHeight 39` in Chromium, across the whole row including the preview cell's
 * icons. The last row draws no bottom border and still measures 40. `getTotalSize()` is `rows.length * ROW_HEIGHT`, so being 1px over here
 * costs 1px of phantom scroll PER ROW — 20 000px of unreachable space at the
 * record cap.
 *
 * Do NOT copy the arithmetic from `FieldsEditor` or `TaxonomyListEditor`, which
 * pair `h-9` with 37. Those put the height on an INNER element inside a `p-0`
 * cell, so the border really does sit outside it and 36 + 1 is right for them.
 * This grid heights the cell itself. Same-looking constant, different geometry —
 * and reading theirs across is how this one was 37 while its rows measured 36.
 *
 * Known 1px: the free-text and searchable editors put an `h-10` input inside the
 * cell's 39px content box, and a cell height is a MINIMUM, so the row being edited
 * measures 41. One row, only while open. Making it flush belongs on the inner
 * input, not on this constant.
 */
const ROW_HEIGHT = 40;

const EM_DASH = '—';
/** Below this, rendering every row is cheaper than windowing. */
const VIRTUALIZE_THRESHOLD = 50;

const MIN_FIELD_COLUMN_WIDTH = 144;
const MIN_PREVIEW_COLUMN_WIDTH = 220;
/** The checkbox (`w-11`) and row-number (`w-14`) columns. */
const FROZEN_COLUMNS_WIDTH = 44 + 56;

function fieldColumnWidth(field: TaxonomyField): number {
  return Math.max(MIN_FIELD_COLUMN_WIDTH, field.valueWidth ?? 0);
}
const DEFAULT_ADD_COUNT = 10;
/** Mirrors `MAX_BULK_DELETE_IDS` in the API's DTO bounds. */
const MAX_BULK_DELETE = 500;

export interface TaxonomyRecordGridProps {
  datasetId: string;
  fields: TaxonomyField[];
  settings: TaxonomySettings | undefined;
  outputs: TaxonomyOutput[];
  rules: TaxonomyRule[];
  records: TaxonomyRecord[];
  readOnly: boolean;
  /**
   * Each bound field's vocabulary, resolved from the list it follows.
   *
   * Resolved by the page rather than here: the hook lives in a sibling feature
   * slice, and one feature may not import another.
   */
  resolvedLists: Record<string, ResolvedList>;
  /**
   * Rule evaluation for one record, memoised by the caller.
   *
   * Optional, and the grid keeps its own cache when it is absent — so a caller
   * that does not need the answers behaves exactly as before. Pass it when
   * something ELSE also needs them, which is what stops two caches evaluating the
   * same rules twice: the export dialog on this page used to spend ~310ms doing
   * that against ~30ms for a hit.
   */
  effectiveFor?: EffectiveFieldsResolver;
  /**
   * Where the Save button renders.
   *
   * The page header carries Export and Save together, but the
   * draft Save acts on lives here — so the button is portaled into the page's
   * node rather than the state being lifted to meet it. Absent means render it
   * in place, which is how this grid behaves when mounted on its own.
   */
  saveSlot?: HTMLElement | null;
  /** Drawn before the record count, for the page's own controls such as reopening its agent. */
  leading?: ReactNode;
  /** Lets the sheet grow with its rows up to the height its parent gives it, then scroll. */
  fill?: boolean;
  /**
   * Tells the page whether anything is unsaved.
   *
   * Outbound only, and deliberately not the draft itself: the export reads what is
   * STORED, so the page needs to say that unsaved edits are not in the file — and
   * that is one boolean, not the eight props hoisting `useRecordDraft` would cost.
   */
  onDirtyChange?: (dirty: boolean) => void;
  /**
   * Which rows have unsaved edits, for the page's agent: a row it writes while the person is
   * editing it keeps its old revision, so their Save conflicts instead of overwriting it.
   */
  onDirtyIdsChange?: (ids: ReadonlySet<string>) => void;
  /** Rows to mark briefly, such as the ones the agent just changed. */
  highlightedIds?: ReadonlySet<string>;
  /** Tracking codes this schema has already issued, for the spend confirmation. */
  codesIssued?: number;
  /**
   * Called after a mint settles, whether it succeeded or failed.
   *
   * The remaining allowance lives on the TAXONOMY, not the dataset, so nothing
   * this slice invalidates reaches it and the confirmation went on quoting a
   * pre-mint number. Raised to the page rather than handled here because an
   * entity may not import another entity's query keys by value, and the page is
   * what knows the taxonomy id.
   *
   * Called on failure too: the server allocates and commits before it answers, so
   * a response lost in transit still spent.
   */
  onCodesSettled?: () => void;
}

/**
 * The rectangle, as indices PLUS the records those indices were chosen against.
 *
 * The indices stay the working representation — geometry, keyboard movement,
 * `aria-activedescendant` and the paste offsets all need them, and deriving them
 * per render would cost a lookup per cell. The ids exist only so a change to
 * `rows` can be reconciled: without them a delete left the indices addressing
 * DIFFERENT records, and the next write landed on a row the user never selected.
 */
interface Selection {
  ar: number;
  ac: number;
  fr: number;
  fc: number;
  anchorRecordId: string;
  focusRecordId: string;
}

const clamp = (n: number, lo: number, hi: number) => Math.min(Math.max(n, lo), hi);

/**
 * Whether an event belongs to the grid itself rather than to something inside it.
 *
 * `focusin`, `keydown`, `copy` and `paste` all bubble, and the grid element now
 * owns all four — so a focused DESCENDANT shares them. Every row has a select
 * checkbox, which Tessra renders as a `<button role="checkbox">`: ticking one to
 * use the bulk toolbar left focus on that button, seeded a selection rectangle
 * on cell 0-0, and the next Delete blanked a cell of a record the user had not
 * touched. The cell editor is the one descendant that must keep its own keys and
 * clipboard.
 */
function isGridEvent(event: { target: EventTarget | null; currentTarget: EventTarget | null }) {
  return event.target === event.currentTarget;
}

/** The cell editor, which owns its own typing, copying and pasting. */
function isEditorEvent(target: EventTarget | null) {
  return target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement;
}

/**
 * Whether a CLIPBOARD event belongs to the sheet.
 *
 * Clipboard events cannot use {@link isGridEvent}. A browser dispatches `copy`
 * and `paste` at the selection's anchor node — measured in Chrome, `paste` arrives
 * with `target` set to the `<span>` inside the focused cell while
 * `document.activeElement` is the grid itself — so requiring `target ===
 * currentTarget` rejected every real paste, and the grid silently ignored the
 * clipboard entirely. Keydown does not have this problem: it goes to
 * `activeElement`, which is the grid.
 *
 * A row's checkbox lives in a `<td>` with no `data-cell`, so it still cannot
 * drive the sheet.
 */
function isCellClipboardEvent(event: {
  target: EventTarget | null;
  currentTarget: EventTarget | null;
}) {
  if (event.target === event.currentTarget) {
    return true;
  }
  return event.target instanceof Element && Boolean(event.target.closest('td[data-cell]'));
}

const MOVES: Record<string, [number, number]> = {
  ArrowUp: [-1, 0],
  ArrowDown: [1, 0],
  ArrowLeft: [0, -1],
  ArrowRight: [0, 1],
};

/**
 * Keys that seed a selection when the grid is focused but nothing is selected.
 *
 * Only keys the handler actually acts on. `Home`/`End` were here without a
 * branch, so the first press swallowed the browser's scroll-to-end and every
 * later press let it through — worse than not handling them at all.
 */
const NAVIGATION_KEYS = new Set([
  ...Object.keys(MOVES),
  // eslint-disable-next-line i18next/no-literal-string -- key names, not copy
  ...['Enter', 'F2'],
]);

/**
 * The records spreadsheet.
 *
 * The selection rectangle, keyboard navigation and TSV clipboard are the schema
 * builder's `FieldsEditor` engine, lifted to a 1:1 column model — there every
 * field spans two sub-columns (value and code) and the coordinate space is
 * `fields.length * 2`, which is baked into five places. Here a column is a
 * field, so that arithmetic disappears.
 */
export function TaxonomyRecordGrid({
  datasetId,
  fields,
  settings,
  outputs,
  rules,
  records,
  readOnly,
  resolvedLists,
  effectiveFor,
  saveSlot,
  leading,
  fill = false,
  onDirtyChange,
  onDirtyIdsChange,
  highlightedIds,
  codesIssued = 0,
  onCodesSettled,
}: TaxonomyRecordGridProps) {
  const { t } = useTranslation('knowledge');
  const gridRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const [sel, setSel] = useState<Selection | null>(null);
  /**
   * Which cell's editor is open, held here rather than in the cell.
   *
   * Enter and F2 open the focused cell, and the grid is the only thing that
   * knows which cell that is.
   *
   * Keyed on the RECORD and the field, not on grid coordinates: a background
   * refetch, an append or a delete above the row reorders the indices, and an
   * index-keyed editor would silently close on the record being typed into and
   * reopen on whichever record now sits at that position.
   */
  const [editingCell, setEditingCell] = useState<{ recordId: string; fieldId: string } | null>(
    null,
  );
  /** The cell the arrow keys just moved to, scrolled in by an effect below. */
  const [scrollTo, setScrollTo] = useState<{ r: number; c: number } | null>(null);
  const tableRef = useRef<HTMLTableElement>(null);
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const [addCount, setAddCount] = useState(DEFAULT_ADD_COUNT);
  const dragging = useRef(false);

  /**
   * Clears the drag flag when the button comes up ANYWHERE.
   *
   * Without this the flag was set on a cell's mousedown and never cleared, so
   * `onMouseEnter` went on extending the rectangle for the rest of the session:
   * one click, then an idle mouse move across the grid, and the selection
   * silently covered every cell the cursor had crossed. The next Delete or paste
   * then wrote over all of them.
   *
   * On `window` rather than the grid, because the button is frequently released
   * outside it, and `pointerup` alongside `mouseup` because a cancelled pointer
   * gesture (a touch drag leaving the element) never produces `mouseup` at all.
   * This mirrors `FieldsEditor`, the engine this selection model was lifted
   * from — that half of it did not come across.
   */
  useEffect(() => {
    const release = () => {
      dragging.current = false;
    };
    window.addEventListener('mouseup', release);
    window.addEventListener('pointerup', release);
    return () => {
      window.removeEventListener('mouseup', release);
      window.removeEventListener('pointerup', release);
    };
  }, []);

  const columns = useMemo(() => gridColumns(fields), [fields]);

  const previewOutputs = useMemo(() => orderedOutputs(outputs), [outputs]);

  const assembleOptions = useMemo(
    () => ({
      fieldDelimiter: settings?.fieldDelimiter ?? undefined,
      subfieldDelimiter: settings?.subfieldDelimiter ?? undefined,
      resolvedValues: resolvedLists,
      now: new Date(),
    }),
    [settings?.fieldDelimiter, settings?.subfieldDelimiter, resolvedLists],
  );

  const assemble = useCallback(
    (
      cells: TaxonomyRecordCells,
    ): { assembledName: string | null; outputs: TaxonomyRecordOutputs } =>
      assembleRecord({ fields, rules, outputs, ...assembleOptions }, cells),
    [fields, rules, outputs, assembleOptions],
  );

  const draft = useRecordDraft({ datasetId, records, assemble });
  const rows = draft.rows;

  /**
   * The Save gate's decision, readable by a handler that outlives the render it
   * was created in.
   *
   * The mint's `onSuccess` saves the codes it just minted, and it runs after a
   * round trip from a callback declared long before the gate is computed - so it
   * cannot read `heldBack` directly. Passing this is what puts the gate's
   * decision in the CALL. It was previously guaranteed only by `BulkToolbar`
   * disabling the mint whenever the draft is dirty; that is a real guarantee, but
   * it lives in a sibling component's prop, and narrowing it would send a
   * defective or duplicate-named row into a single-transaction batch the unique
   * index refuses whole. Same idiom as `dirtyRef` in `useRecordDraft`.
   */
  const heldBackRef = useRef<ReadonlySet<string>>(new Set());

  /**
   * Blocks an in-app navigation away from unsaved records.
   *
   * The hook's `beforeunload` handler covers a refresh or a tab close; this
   * covers a route change, which `beforeunload` never sees. Both are needed, and
   * they are not optional: explicit Save means leaving discards the work, and
   * discarding it silently is the prototype's worst bug.
   */
  const blocker = useBlocker(draft.isDirty);

  /**
   * The field a "Generate codes" click mints for.
   *
   * The FIRST computed field carrying a pattern. A schema may declare more than
   * one, and choosing between them needs a control this toolbar does not have —
   * so rather than mint for an arbitrary one, the picker is a gap named in the
   * docs. One is the case every real schema has.
   */
  const codeField = useMemo(
    () =>
      columns.find((c) => c.field.type === 'computed' && (c.field.pattern?.length ?? 0) > 0)?.field,
    [columns],
  );

  /**
   * Cells no copy may inherit: assembled by the schema or issued by the mint,
   * never typed. Read off the schema's FIELDS rather than the grid's columns, so
   * a field hidden from the grid is treated the same as one shown.
   */
  const derivedFieldIds = useMemo(
    () =>
      new Set(fields.filter((f) => f.type === 'computed' || f.type === 'linked').map((f) => f.id)),
    [fields],
  );
  const generateCodes = useGenerateRecordCodes(datasetId);
  const [confirmCodes, setConfirmCodes] = useState(false);

  /**
   * Which bulk action is waiting to be confirmed, or null.
   *
   * Both actions empty work the user typed and NEITHER is recoverable: Clear is
   * a persisted write rather than a draft edit, and no record-level history
   * exists to restore from. What separates them is the row id, which is the
   * `assetKey` a tracking code is minted against - so a cleared row can be
   * re-minted for free and a deleted one strands its code against the schema's
   * lifetime cap. The dialog copy is the only place that difference is stated.
   */
  const [confirmBulk, setConfirmBulk] = useState<'clear' | 'delete' | null>(null);

  /**
   * The selected records that would actually SPEND a code.
   *
   * Not the selection size. A record that already holds a code costs nothing to
   * ask for again, so counting the selection told someone that picking 200 rows
   * of which 199 are coded would use 200 codes. The number in a confirmation for
   * an irreversible action has to be the real one.
   */
  const spendingRecords = useMemo(
    () => (codeField ? rows.filter((r) => selectedRows.has(r.id) && !r.cells[codeField.id]) : []),
    [codeField, rows, selectedRows],
  );

  /**
   * Mints for the selection, then saves the codes with the pending edits.
   *
   * `draft.save(extra)` rather than writing the cells and saving separately: a
   * code is already spent by the time it arrives, so leaving it in a discardable
   * draft would let the grid disagree with what the schema has issued.
   *
   * Records that already hold a code are still sent. The mint is anchored on the
   * record id, so they cost nothing and come back with the code they already
   * have — filtering them here would only duplicate a rule the server owns.
   */
  const runGenerateCodes = useCallback(() => {
    setConfirmCodes(false);
    if (!codeField) {
      return;
    }
    generateCodes.mutate(
      { ids: Array.from(selectedRows), fieldId: codeField.id },
      {
        onSettled: () => onCodesSettled?.(),
        onSuccess: (result) => {
          if (result.minted.length > 0) {
            draft.save(
              result.minted.map(({ id, code }) => ({
                recordId: id,
                fieldId: codeField.id,
                value: code,
              })),
              heldBackRef.current,
            );
          }

          /**
           * `spent`, measured server-side, not the size of the returned list.
           * A record already holding a code is answered without allocating, so
           * the list length reports codes burned that are still available — of a
           * resource nothing releases.
           */
          if (result.spent > 0) {
            enqueueSnackbar(t('taxonomy.grid.codesGenerated', { count: result.spent }), {
              variant: 'success',
            });
          } else if (result.minted.length > 0) {
            enqueueSnackbar(t('taxonomy.grid.codesAlreadyGenerated'), { variant: 'info' });
          }

          /**
           * The server's reason, not a bare count. A whole selection can be
           * refused for one fixable cause — a record missing a value its pattern
           * reads, or a bound list that was never published — and "3 records did
           * not get a code" leaves the user with nothing to act on.
           *
           * TRANSLATED, not interpolated: the wire codes went straight into the
           * sentence, so the message read "…did not get a code
           * (record_incomplete)".
           */
          if (result.skipped.length > 0) {
            const reasons = new Set(result.skipped.map((sk) => sk.reason));
            const incompleteOnly = reasons.size === 1 && reasons.has('record_incomplete');
            enqueueSnackbar(
              incompleteOnly
                ? t('taxonomy.grid.codesSkippedIncomplete', { count: result.skipped.length })
                : t('taxonomy.grid.codesSkipped', {
                    count: result.skipped.length,
                    reasons: Array.from(reasons)
                      .map((reason) => skipReason(t, reason))
                      .join('; '),
                  }),
              { variant: 'warning' },
            );
          }
        },
      },
    );
  }, [codeField, generateCodes, selectedRows, draft, t, onCodesSettled]);

  const append = useAppendRecords(datasetId);
  const clear = useClearRecords(datasetId);
  const remove = useDeleteRecords(datasetId);

  /** Effective options, visibility and required-ness per record. */
  const ownResolver = useEffectiveFields(fields, rules, resolvedLists);
  const resolveEffective = effectiveFor ?? ownResolver;
  const effective = useMemo(() => rows.map(resolveEffective), [rows, resolveEffective]);

  /**
   * Fields any rule can make required, for the column header.
   *
   * The header is per column and `require_field` is per record, so the asterisk
   * marks "can be required" while the per-cell marker shows where it actually is.
   * Reading `field.required` alone left a rule-required column unmarked entirely.
   */
  const requiredByRule = useMemo(
    () =>
      new Set(
        effective.flatMap((fieldsFor) =>
          Object.entries(fieldsFor)
            .filter(([, state]) => state.required)
            .map(([fieldId]) => fieldId),
        ),
      ),
    [effective],
  );

  const shouldVirtualize = rows.length > VIRTUALIZE_THRESHOLD;
  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ROW_HEIGHT,
    /*
     * No `scrollMargin`, unlike the two sibling grids. The sticky `<thead>` lives
     * inside this same scroller, so it occupies scroll space above row 0 and
     * `scrollToIndex` lands about a header's height short (`h-16`). `overscan: 12` covers it,
     * so the row is always mounted and the miss reads as a small offset rather
     * than a blank band. Left as is rather than fixed blind: the correct margin is
     * the rendered header height, which needs a measurement pass this grid
     * deliberately avoids.
     */
    overscan: 12,
    enabled: shouldVirtualize,
  });

  const rect = sel
    ? {
        r0: Math.min(sel.ar, sel.fr),
        r1: Math.max(sel.ar, sel.fr),
        c0: Math.min(sel.ac, sel.fc),
        c1: Math.max(sel.ac, sel.fc),
      }
    : null;
  const inRect = (r: number, c: number) =>
    !!rect && r >= rect.r0 && r <= rect.r1 && c >= rect.c0 && c <= rect.c1;

  const valueAt = useCallback(
    (r: number, c: number): string => {
      const column = at(columns, c);
      const row = at(rows, r);
      if (!column || !row) {
        return '';
      }
      const raw = row.cells[column.field.id];
      return Array.isArray(raw) ? raw.join(',') : (raw ?? '');
    },
    [columns, rows],
  );

  /**
   * A pasted token mapped to a value this cell is actually allowed to hold.
   *
   * Returns `null` when the column is governed and the token is not in its
   * vocabulary. That refusal is the point: a dropdown cell can only be SET to an
   * allowed value through its editor, but a paste wrote clipboard text straight
   * through — so `NOT_A_REAL_REGION` was stored, and `codeFor` emits an unmatched
   * value verbatim, putting it into `assembledName`. That column is a permanent
   * reporting join key under a unique index, so an ungoverned paste does not
   * produce a display glitch; it produces a name nothing can join on.
   *
   * A CODE is accepted as well as a display value, and stored as the display
   * value: the cell shows the code, so that is what people read and type back.
   *
   * Small differences such as case and accents are ignored; `matchVocabulary`
   * has the rules. It runs on the field's whole vocabulary and skips options
   * the rules forbid for THAT record. Matching on the narrowed set alone let a
   * value a rule forbids resolve to a near-twin the rule allows.
   */
  const coerceToVocabulary = useCallback(
    (r: number, column: GridColumn, value: string): string | null => {
      const constrained = column.field.type === 'dropdown' || Boolean(column.field.listRef?.listId);
      if (!constrained) {
        return value;
      }
      const perRecord = at(effective, r);
      const narrowed = perRecord ? get(perRecord, column.field.id)?.options : undefined;
      const match = matchVocabulary(
        vocabularySource(column.field, resolvedLists),
        value,
        narrowed ? (option) => narrowed.includes(option) : undefined,
      );
      return match ? match.value : null;
    },
    [effective, resolvedLists],
  );

  const writeAt = useCallback(
    (r: number, c: number, value: string | null) => {
      const column = at(columns, c);
      const row = at(rows, r);
      if (!column?.editable || !row) {
        return null;
      }
      /*
       * A cell a rule has HIDDEN is not writable either.
       *
       * The cell's own `editable` prop is `column.editable && !readOnly &&
       * !state.hidden`, but this guard only checked the column — so a paste or a
       * Delete over a hidden cell stored a value nobody could see, which
       * reappeared if the governing rule later fired. `commitCell`'s own
       * `set_default` cascade already checks this; the rectangle paths did not.
       */
      if (get(at(effective, r) ?? {}, column.field.id)?.hidden) {
        return null;
      }
      if (typeof value === 'string' && value !== '') {
        const allowedValue = coerceToVocabulary(r, column, value);
        if (allowedValue === null) {
          return null;
        }
        value = allowedValue;
      }
      return {
        recordId: row.id,
        fieldId: column.field.id,
        /**
         * Clamped here rather than only in the inline editor, which has a
         * `maxLength`. A paste writes clipboard text straight through, and the
         * DTO validates `patch` as an object with no per-value length rule — so
         * one pasted 10 KB Excel cell would push the record's document past the
         * ~2 KB TOAST threshold, where a single later cell edit costs 42x the WAL.
         *
         * `clampToLength` drops a split surrogate pair rather than slicing
         * through one, which is why it exists rather than a bare `.slice`.
         */
        value: typeof value === 'string' ? clampToLength(value, MAX_CELL_VALUE_LENGTH) : value,
      };
    },
    [columns, rows, effective, coerceToVocabulary],
  );

  /**
   * The cascade `commitCell` runs, for one row, as writes rather than a commit.
   *
   * Extracted so a fill produces the same data typing the value would. A paste
   * deliberately skips it, so before this a filled column and a typed column
   * could differ on any schema using `set_default`.
   */
  const cascadeFor = useCallback(
    (row: TaxonomyRecord, fieldId: string, next: string | null) => {
      const cells = { ...row.cells, [fieldId]: next };
      const after = applyRules(fields, rules, cells, resolvedLists);
      return Object.entries(after)
        .filter(([id, state]) => {
          if (id === fieldId || !state.suggestedDefault || state.hidden) {
            return false;
          }
          const existing = cells[id];
          return existing === undefined || existing === null || existing === '';
        })
        .map(([id, state]) => ({
          recordId: row.id,
          fieldId: id,
          value: state.suggestedDefault as string,
        }));
    },
    [fields, rules, resolvedLists],
  );

  /**
   * Writes a cell, then applies any `set_default` a rule now asks for.
   *
   * `set_default` is one of the four rule actions this slice existed to
   * activate, and it only means anything at the moment a rule starts firing —
   * which is the moment some other cell changed. Applied only to a cell that is
   * still EMPTY, so it prefills rather than overwriting a deliberate choice, and
   * in the same batch as the edit that triggered it so the two are one undoable
   * step rather than two.
   */
  const commitCell = useCallback(
    (row: TaxonomyRecord, fieldId: string, next: string | null) => {
      draft.setCells([
        { recordId: row.id, fieldId, value: next },
        ...cascadeFor(row, fieldId, next),
      ]);
    },
    [draft, cascadeFor],
  );

  /**
   * Writes one value down a whole column.
   *
   * SCOPE: the selected rows, or every row when nothing is selected. That keeps
   * the expensive case opt-in — a fill at the 20 000-record cap is two rule
   * evaluations per row and leaves every one of them dirty, with no undo — while
   * "set this column for all three of my rows" still works with no selection.
   *
   * Routed through `writeAt`, so a fill refuses exactly what a paste refuses:
   * computed and linked columns, cells a rule has hidden for THAT record, and
   * values outside a rules-narrowed vocabulary. `readOnly` is checked here because
   * `writeAt` does not — only the paste and Enter handlers ever did.
   *
   * What it drops is reported rather than absorbed, keeping paste's split between
   * "not in the vocabulary" and "a rule hides it": one number covering both would
   * tell someone to fix the wrong thing.
   */
  const fillColumn = useCallback(
    (columnIndex: number, source: { kind: 'value'; value: string } | { kind: 'first' }) => {
      if (readOnly) {
        return;
      }
      const scoped = selectedRows.size > 0 ? rows.filter((row) => selectedRows.has(row.id)) : rows;
      if (scoped.length === 0) {
        return;
      }

      const column = at(columns, columnIndex);
      if (!column) {
        return;
      }

      const value =
        source.kind === 'value'
          ? source.value
          : (() => {
              const first = scoped[0];
              const raw = first.cells[column.field.id];
              return Array.isArray(raw) ? (raw[0] ?? '') : (raw ?? '');
            })();

      /*
       * Nothing to copy is not a licence to clear the column.
       *
       * `''` skips `writeAt`'s coercion guard, so it used to be written to every
       * row in scope — up to the whole dataset, with no undo, from one menu click
       * on a grid whose first row is empty. That is the state a brand-new taxonomy
       * is in, which makes it the likeliest first use of this menu.
       */
      if (source.kind === 'first' && value === '') {
        enqueueSnackbar(t('taxonomy.grid.fillNothingToCopy'), { variant: 'info' });
        return;
      }

      const writes: CellWrite[] = [];
      let hiddenByRule = 0;
      let refused = 0;
      let unchanged = 0;
      // `indexOf` per row made the fill O(n²) — ~2x10^8 comparisons at the cap.
      const indexOfRow = new Map(rows.map((row, index) => [row.id, index]));
      const listSpelling =
        matchVocabulary(vocabularySource(column.field, resolvedLists), value)?.value ?? value;
      for (const row of scoped) {
        const r = indexOfRow.get(row.id) ?? -1;
        /*
         * A row already holding the value is left ALONE rather than rewritten.
         *
         * `writeAt` never compares against what is there, so a fill used to dirty
         * every row in scope even when it changed nothing — bumping revisions,
         * inflating the PATCH, and dragging rows the user never touched into the
         * held-back set. The comparison is against the value `writeAt` would store,
         * so a first row spelled `facebook` converts itself and every other row to
         * the list's spelling. A row `writeAt` refuses (hidden, or the value is
         * forbidden for it) that already holds the value, in either spelling, is
         * also unchanged rather than skipped.
         */
        const raw = row.cells[column.field.id];
        const current = Array.isArray(raw) ? (raw[0] ?? '') : (raw ?? '');
        const write = writeAt(r, columnIndex, value);
        if (write ? write.value === current : current === value || current === listSpelling) {
          unchanged += 1;
          continue;
        }
        if (!write) {
          if (get(at(effective, r) ?? {}, column.field.id)?.hidden) {
            hiddenByRule += 1;
          } else {
            refused += 1;
          }
          continue;
        }
        writes.push(write, ...cascadeFor(row, column.field.id, write.value));
      }

      if (writes.length > 0) {
        draft.setCells(writes);
      } else if (unchanged > 0 && hiddenByRule === 0 && refused === 0) {
        // Otherwise a fill over a column that already holds the value looks broken.
        enqueueSnackbar(t('taxonomy.grid.fillAlreadySet'), { variant: 'info' });
      }
      if (hiddenByRule > 0) {
        enqueueSnackbar(t('taxonomy.grid.fillHiddenByRule', { count: hiddenByRule }), {
          variant: 'info',
        });
      }
      if (refused > 0) {
        enqueueSnackbar(t('taxonomy.grid.fillRefused', { count: refused }), { variant: 'info' });
      }
    },
    [
      readOnly,
      selectedRows,
      rows,
      columns,
      effective,
      resolvedLists,
      writeAt,
      cascadeFor,
      draft,
      t,
    ],
  );

  /**
   * Re-points the rectangle at the records it was chosen against, whenever the
   * row set changes.
   *
   * The indices are positional, so a delete elsewhere silently re-aims them: the
   * grid kept reporting a pending change after the edited row was gone, and the
   * next Save wrote that edit onto whichever record had moved into the index —
   * cross-record corruption with no error. Reproduced in
   * `TaxonomyRecordGrid.selection.test.tsx`.
   *
   * Re-pointed rather than cleared, because a refetch that merely reorders rows
   * is routine and dropping the selection on every one of them would be felt
   * constantly. Cleared only when a record is genuinely gone, since there is then
   * nothing honest to point at.
   *
   * Computed OUTSIDE the `setSel` updater, and bails before setting anything when
   * the indices already match. An updater must be pure — React may re-invoke or
   * discard it, and does under StrictMode — so the queued scroll cannot live in
   * one, which is the same rule the `scrollTo` effect below records. Depending on
   * `sel` costs one extra pass after a move, which then matches and returns.
   */
  useEffect(() => {
    if (!sel) {
      return;
    }
    const ar = rows.findIndex((r) => r.id === sel.anchorRecordId);
    const fr = rows.findIndex((r) => r.id === sel.focusRecordId);
    if (ar === -1 || fr === -1) {
      setSel(null);
      return;
    }
    if (ar === sel.ar && fr === sel.fr) {
      return;
    }
    setSel({ ...sel, ar, fr });
    /*
     * Bring the moved selection back into view.
     *
     * Under virtualisation the anchored row can land outside the rendered window,
     * and then `aria-activedescendant` points at `taxonomy-cell-<fr>-<fc>` — an id
     * that is not in the document, which is an outright ARIA violation and leaves
     * a screen reader with no active cell. The user sees no highlight either.
     */
    setScrollTo({ r: fr, c: sel.fc });
  }, [rows, sel]);

  const moveFocus = (dr: number, dc: number, extend = false) => {
    if (!sel) {
      return;
    }
    const nr = clamp(sel.fr + dr, 0, Math.max(0, rows.length - 1));
    const nc = clamp(sel.fc + dc, 0, Math.max(0, columns.length - 1));
    const movedId = at(rows, nr)?.id ?? sel.focusRecordId;
    setSel(
      extend
        ? { ...sel, fr: nr, fc: nc, focusRecordId: movedId }
        : {
            ar: nr,
            ac: nc,
            fr: nr,
            fc: nc,
            anchorRecordId: movedId,
            focusRecordId: movedId,
          },
    );
    setScrollTo({ r: nr, c: nc });
  };

  /**
   * Hands focus back to the grid when an editor closes.
   *
   * The cell commits by blurring its input, and a browser does not delegate
   * focus to a `tabIndex` ancestor — it goes to `document.body`. So the grid
   * kept moving the selection while nothing held focus, and a keyboard-only user
   * was ejected after ONE edit and had to Tab all the way back in. Guarded on
   * the grid still owning focus-or-body, so this never steals focus from a modal
   * or a control the user deliberately tabbed to.
   */
  const wasEditing = useRef(false);
  useEffect(() => {
    const closed = wasEditing.current && editingCell === null;
    wasEditing.current = editingCell !== null;
    if (!closed) {
      return;
    }
    // Only when an editor CLOSED, never on mount: focusing the grid unprompted
    // would take focus from the page and seed a selection nobody asked for.
    const active = document.activeElement;
    if (active === null || active === document.body) {
      tableRef.current?.focus({ preventScroll: true });
    }
  }, [editingCell]);

  /**
   * Scrolls the focused cell in AFTER the selection commits.
   *
   * Two reasons it is not done inside the `setSel` updater, where it started: an
   * updater has to be pure (React may re-invoke or discard it, and under
   * StrictMode does), and the target row may not be in the DOM yet under
   * virtualisation, so the read has to happen once React has rendered it.
   */
  useEffect(() => {
    if (!scrollTo) {
      return;
    }
    // eslint-disable-next-line @typescript-eslint/no-deprecated -- the rule cannot pin the non-deprecated `querySelector` overload for a generic element
    const cell = gridRef.current?.querySelector<HTMLElement>(
      `[data-cell="${scrollTo.r}-${scrollTo.c}"]`,
    );
    const scroller = scrollRef.current;
    if (cell && scroller) {
      // Padded only for this call, so the cell is not left under the frozen columns or the sticky
      // header. A padding on the scroller itself made the browser scroll whenever a sticky
      // checkbox or header button took focus.
      // eslint-disable-next-line @typescript-eslint/no-deprecated -- the rule cannot pin the non-deprecated `querySelector` overload for a tag name
      const headerHeight = scroller.querySelector('thead')?.getBoundingClientRect().height ?? 0;
      // eslint-disable-next-line i18next-templates/no-literal-string -- CSS value
      scroller.style.scrollPadding = `${headerHeight}px 0 0 ${FROZEN_COLUMNS_WIDTH}px`;
      cell.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      scroller.style.scrollPadding = '';
      return;
    }
    /*
     * The row is not rendered, so there is no element to scroll to.
     *
     * A DOM lookup alone made this a silent no-op for any target outside the
     * virtualised window — which is every target that most needs scrolling, and
     * is why an arrow key could not recover a selection the reconcile had moved
     * out of view.
     */
    if (shouldVirtualize) {
      rowVirtualizer.scrollToIndex(scrollTo.r, { align: 'auto' });
    }
  }, [scrollTo, shouldVirtualize, rowVirtualizer]);

  /*
   * A consumed request is cleared, so it cannot be replayed.
   *
   * `scrollTo` is a one-shot command, but it was left set, and the effect above
   * now also depends on `shouldVirtualize` — which flips whenever the record
   * count crosses the virtualisation threshold. Adding a record around that
   * boundary therefore re-ran a scroll the user had asked for much earlier and
   * yanked the viewport away from where they were.
   *
   * A separate effect rather than a clear inside the one above: clearing there
   * would retrigger it through its own `scrollTo` dependency.
   */
  useEffect(() => {
    if (scrollTo) {
      setScrollTo(null);
    }
  }, [scrollTo]);

  useEffect(() => {
    onDirtyChange?.(draft.isDirty);
  }, [draft.isDirty, onDirtyChange]);

  useEffect(() => {
    onDirtyIdsChange?.(draft.dirtyIds);
  }, [draft.dirtyIds, onDirtyIdsChange]);

  const onGridKeyDown = (e: React.KeyboardEvent) => {
    if (isEditorEvent(e.target)) {
      /*
       * Enter inside the editor commits and closes it (the input blurs itself),
       * so the only thing left is the spreadsheet convention of landing on the
       * cell below. Handled here rather than in the cell because the cell has no
       * idea where it sits.
       */
      if (e.key === 'Enter' && sel) {
        moveFocus(1, 0);
      }
      return;
    }
    /*
     * Anything else inside the grid keeps its own keys. Without this a focused
     * row checkbox drove Delete, paste and Enter against a selection the user
     * never made.
     */
    if (!isGridEvent(e)) {
      return;
    }
    // Copy/cut/paste arrive as native clipboard events instead.
    if (e.ctrlKey || e.metaKey) {
      return;
    }
    /*
     * Nothing was reachable by keyboard before this.
     *
     * Every branch below needs a selection, and a selection was only ever
     * created by a mouse — so tabbing to the grid and pressing an arrow key did
     * nothing at all, and the only way into edit mode was a double-click. The
     * first navigation keystroke now seeds the top-left cell and stops there,
     * which is what a spreadsheet does when you tab into it.
     */
    if (!sel) {
      const first = at(rows, 0);
      if (NAVIGATION_KEYS.has(e.key) && first && columns.length > 0) {
        e.preventDefault();
        setSel({
          ar: 0,
          ac: 0,
          fr: 0,
          fc: 0,
          anchorRecordId: first.id,
          focusRecordId: first.id,
        });
      }
      return;
    }
    if (!rect) {
      return;
    }

    // Enter and F2 are the two standard ways into a grid cell's editor.
    if (e.key === 'Enter' || e.key === 'F2') {
      e.preventDefault();
      const column = at(columns, sel.fc);
      const row = at(rows, sel.fr);
      if (!readOnly && column?.editable && row) {
        setEditingCell({ recordId: row.id, fieldId: column.field.id });
        setScrollTo({ r: sel.fr, c: sel.fc });
      }
      return;
    }

    // Arrow navigation stays available while read-only: it is what makes a copy
    // possible without a mouse.
    if (readOnly && !get(MOVES, e.key)) {
      return;
    }

    if ((e.key === 'Delete' || e.key === 'Backspace') && !readOnly) {
      e.preventDefault();
      const writes = [];
      let blockedByColumn = false;
      let hiddenByRule = false;
      for (let r = rect.r0; r <= rect.r1; r += 1) {
        for (let c = rect.c0; c <= rect.c1; c += 1) {
          const w = writeAt(r, c, null);
          if (w) writes.push(w);
          else if (!at(columns, c)?.editable) blockedByColumn = true;
          else hiddenByRule = true;
        }
      }
      draft.setCells(writes);
      // Only when nothing was cleared: then the key appears to do nothing at all.
      if (writes.length === 0 && (blockedByColumn || hiddenByRule)) {
        enqueueSnackbar(
          t(
            blockedByColumn
              ? 'taxonomy.grid.deleteBlockedColumn'
              : 'taxonomy.grid.deleteHiddenByRule',
          ),
          { variant: 'info', preventDuplicate: true },
        );
      }
      return;
    }

    const move = get(MOVES, e.key);
    if (move) {
      e.preventDefault();
      moveFocus(move[0], move[1], e.shiftKey);
    }
  };

  const onGridCopy = (e: React.ClipboardEvent) => {
    // A text selection inside the cell editor is the editor's to copy.
    if (!rect || isEditorEvent(e.target) || !isCellClipboardEvent(e)) {
      return;
    }
    const lines: string[] = [];
    for (let r = rect.r0; r <= rect.r1; r += 1) {
      const cols: string[] = [];
      for (let c = rect.c0; c <= rect.c1; c += 1) {
        cols.push(valueAt(r, c));
      }
      lines.push(cols.join('\t'));
    }
    e.clipboardData.setData('text/plain', lines.join('\n'));
    e.preventDefault();
  };

  const onGridPaste = (e: React.ClipboardEvent) => {
    /*
     * A paste INTO the open editor is the editor's. The grid used to intercept
     * it — `preventDefault` and then spill the block across the rectangle — so
     * pasting a value into a cell you were editing wrote it into the cell's
     * neighbours instead.
     */
    if (!rect || readOnly || isEditorEvent(e.target) || !isCellClipboardEvent(e)) {
      return;
    }
    const text = e.clipboardData.getData('text/plain');
    if (!text) {
      return;
    }
    e.preventDefault();

    const grid = text
      .replace(/\r\n?/g, '\n')
      .split('\n')
      .filter((line, i, all) => !(i === all.length - 1 && line === ''))
      .map((line) => line.split('\t'));

    const writes = [];
    const refused: string[] = [];
    /**
     * Values dropped because the COLUMN cannot take them, which is a different
     * problem from a value the vocabulary rejected and needs its own report.
     * Previously these fell through both branches — `writeAt` returns null for a
     * generated column, and the `refused` guard tested `editable`, so a paste
     * whose block reached the tracking-code column lost that column in silence.
     */
    const blockedByColumn: string[] = [];
    /**
     * Values dropped because a RULE hides that cell on that record.
     *
     * A third bucket rather than a reuse of either: the column is editable, so
     * these fell into `refused`, which tells the user their value "is not one of
     * the allowed options" — a statement about the value, when nothing is wrong
     * with the value. And unlike `blockedByColumn` it is not a property of the column:
     * the same column takes the same value on a record whose rule does not fire.
     */
    const hiddenByRule: string[] = [];
    // A single copied value FILLS the selection; a block SPILLS from its
    // top-left, clipped at the grid's edges.
    const single = grid.length === 1 && grid[0].length === 1;

    /*
     * How much of the block did not fit.
     *
     * Reported rather than absorbed: 8 rows pasted into a 3-record grid wrote 3
     * and dropped 5 with no feedback at all, which reads as a paste that worked.
     * Rows are not appended to make room — a record is drawn against a hard
     * 20 000 cap per taxonomy, so spending five of them is the user's decision.
     */
    const clippedRows = single ? 0 : Math.max(0, rect.r0 + grid.length - rows.length);
    const widest = single ? 0 : Math.max(...grid.map((line) => line.length));
    const clippedColumns = single ? 0 : Math.max(0, rect.c0 + widest - columns.length);

    for (let r = rect.r0; r <= (single ? rect.r1 : rect.r0 + grid.length - 1); r += 1) {
      for (
        let c = rect.c0;
        c <= (single ? rect.c1 : rect.c0 + (grid[r - rect.r0]?.length ?? 1) - 1);
        c += 1
      ) {
        if (r >= rows.length || c >= columns.length) {
          continue;
        }
        const raw = single
          ? at(at(grid, 0) ?? [], 0)
          : at(at(grid, r - rect.r0) ?? [], c - rect.c0);
        if (raw === undefined) {
          continue;
        }
        const w = writeAt(r, c, raw === '' ? null : raw);
        if (w) {
          writes.push(w);
        } else if (raw !== '') {
          // Counted either way: silently dropping cells reads as a paste that
          // worked, which is how a half-pasted block goes unnoticed. Which
          // bucket matters, because the three take different actions — a
          // rejected value can be corrected, a generated column cannot, and a
          // rule-hidden cell needs the rule's condition changed first.
          const column = at(columns, c);
          if (!column?.editable) {
            blockedByColumn.push(raw);
          } else if (get(at(effective, r) ?? {}, column.field.id)?.hidden) {
            hiddenByRule.push(raw);
          } else {
            refused.push(raw);
          }
        }
      }
    }
    draft.setCells(writes);

    if (refused.length > 0) {
      enqueueSnackbar(
        t('taxonomy.grid.pasteRefused', { count: refused.length, example: refused[0] }),
        { variant: 'warning' },
      );
    }

    if (blockedByColumn.length > 0) {
      enqueueSnackbar(t('taxonomy.grid.pasteBlockedColumns', { count: blockedByColumn.length }), {
        variant: 'warning',
      });
    }

    if (hiddenByRule.length > 0) {
      enqueueSnackbar(t('taxonomy.grid.pasteHiddenByRule', { count: hiddenByRule.length }), {
        variant: 'warning',
      });
    }

    // Both axes, not whichever is larger: a block that overflows in both
    // directions used to report only its rows, which is the same silence one
    // axis narrower.
    if (clippedRows > 0) {
      enqueueSnackbar(t('taxonomy.grid.pasteClippedRows', { count: clippedRows }), {
        variant: 'warning',
      });
    }
    if (clippedColumns > 0) {
      enqueueSnackbar(t('taxonomy.grid.pasteClippedColumns', { count: clippedColumns }), {
        variant: 'warning',
      });
    }
  };

  const toggleRow = (id: string) =>
    setSelectedRows((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const allSelected = rows.length > 0 && selectedRows.size === rows.length;

  const selectedRecords = rows.filter((r) => selectedRows.has(r.id));

  const runClear = useCallback(() => {
    setConfirmBulk(null);
    clear.mutate(
      selectedRecords.map((r) => ({ id: r.id, expectedRevision: r.revision })),
      {
        onSuccess: (result) => {
          // Only what the server actually emptied. The local draft has to
          // forget those edits or the patch is merged back over the refetch and
          // the cleared cell re-displays the value the user just cleared - but a
          // row the server REFUSED still holds its value, so dropping its edit
          // would discard a real change.
          draft.dropLocalEdits(result.applied.map((r) => r.id));
          // Clear checks the revision the client held, exactly as Save does, and
          // can be refused for exactly the same reason. Saying nothing left the
          // user with a button that appeared to do nothing.
          if (result.conflicted.length > 0) {
            draft.reportConflicts(result.conflicted);
          }
        },
      },
    );
  }, [clear, draft, selectedRecords]);

  const runDelete = useCallback(() => {
    setConfirmBulk(null);
    // Chunked to the API's bulk-delete ceiling. The grid can select every row up
    // to the 20 000 record cap, so a select-all delete on a large taxonomy would
    // otherwise be refused wholesale by the DTO bound.
    const ids = selectedRecords.map((r) => r.id);
    for (let i = 0; i < ids.length; i += MAX_BULK_DELETE) {
      remove.mutate(ids.slice(i, i + MAX_BULK_DELETE));
    }
    setSelectedRows(new Set());
  }, [remove, selectedRecords]);

  const virtualRows = shouldVirtualize
    ? rowVirtualizer.getVirtualItems()
    : rows.map((_, i) => ({ index: i, key: i, start: 0, size: ROW_HEIGHT, end: ROW_HEIGHT }));

  /**
   * The height the rendered window is NOT occupying, above and below.
   *
   * Without these the tbody is only as tall as the ~24 rows currently rendered,
   * so the scroll container has nothing to scroll into: measured at 1 000
   * records it topped out at 1 753px and the grid never rendered past row 49 —
   * virtualisation broke exactly the case it exists for, and every row beyond it
   * was unreachable rather than merely slow.
   *
   * Rendered as `<tr>` rather than by transforming rows, because a table's row
   * geometry is the browser's to compute; absolutely positioning `<tr>`s inside
   * a `<tbody>` is what the schema builder's grid had to avoid too.
   */
  const padTop = shouldVirtualize && virtualRows.length > 0 ? (virtualRows[0]?.start ?? 0) : 0;
  const padBottom =
    shouldVirtualize && virtualRows.length > 0
      ? rowVirtualizer.getTotalSize() - (virtualRows[virtualRows.length - 1]?.end ?? 0)
      : 0;

  /**
   * Every column, so a spacer row spans the full table.
   *
   * The leading 2 is the checkbox and the row-number columns, which live outside
   * `columns` — deliberately, since every coordinate in this file (`data-cell`,
   * paste offsets, `moveFocus` clamps) is an index into `columns`, and a frozen
   * column inside that array would shift all of them.
   */
  const totalColumns = 2 + columns.length + previewOutputs.length;

  /*
   * Under `table-fixed` a `<th>`'s `min-width` is ignored and `w-full` shares the scroller out
   * equally, so a wide schema squeezes every column instead of scrolling. Field columns get fixed
   * widths; the preview columns get none, so they take any spare room and the frozen columns keep
   * the widths their `sticky` offsets assume.
   */
  // eslint-disable-next-line i18next-templates/no-literal-string -- CSS value
  const tableWidth = `max(${
    FROZEN_COLUMNS_WIDTH +
    columns.reduce((sum, column) => sum + fieldColumnWidth(column.field), 0) +
    previewOutputs.length * MIN_PREVIEW_COLUMN_WIDTH
  }px, 100%)`;

  /**
   * What is wrong with each row, cached on the row object.
   *
   * Cached for the same reason `effective` is: every keystroke makes a new `rows`
   * array while only the edited row is a new object, and `cost.test.tsx` bounds
   * the rule evaluations a single edit may cost. Invalidated by the same set,
   * since a defect is a function of the columns and the effective fields.
   */
  const defectCache = useMemo(
    () => new WeakMap<TaxonomyRecord, Map<string, CellDefect>>(),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- these invalidate the map rather than being read
    [columns, resolveEffective],
  );

  const defectsFor = useCallback(
    (row: TaxonomyRecord) => {
      const cached = defectCache.get(row);
      if (cached) {
        return cached;
      }
      const next = rowDefects(columns, resolveEffective(row), row.cells);
      defectCache.set(row, next);
      return next;
    },
    [defectCache, columns, resolveEffective],
  );

  /**
   * A defect as the sentence shown on the cell.
   *
   * The marker and the Save gate read the SAME map, which is what stops a cell
   * showing a triangle the gate ignores, or the reverse.
   */
  const defectMessage = useCallback(
    (defect: CellDefect | undefined, column: GridColumn) => {
      if (defect === 'breaks_pattern') {
        return t('taxonomy.grid.breaksPattern', {
          pattern: column.field.validationPattern ?? '',
        });
      }
      return defect === 'required_empty' ? t('taxonomy.grid.requiredEmpty') : null;
    },
    [t],
  );

  /**
   * A column's vocabulary, for the fill menu's "set to" list.
   *
   * Column-level rather than per-record, so it cannot narrow by `limit_options` —
   * a fill that a rule then refuses on some rows reports those rows rather than
   * pretending the option was never offered.
   *
   * A LIST-BOUND column never falls back to its inline snapshot. That snapshot is
   * stale by design, and offering a value the schema no longer accepts is worse
   * than offering none — the same rule the export's dropdowns follow.
   */
  const columnOptions = useCallback(
    (column: GridColumn): TaxonomyFieldValue[] => {
      const resolved = Object.prototype.hasOwnProperty.call(resolvedLists, column.field.id)
        ? resolvedLists[column.field.id].values
        : undefined;
      /*
       * Through `usableOptions`, like every other consumer of a vocabulary, and
       * on BOTH branches.
       *
       * The builder permits publishing a field whose values include blanks or
       * repeats, so the raw array offers a blank row that WIPES the column when
       * picked, a duplicate that collides on React's key, and a whitespace entry
       * the write then always refuses. A shared list can repeat a value too -
       * nothing checks that on publish - and the bound branch used to return it
       * raw, so a duplicate offered two entries that both write the same cell
       * while only the first spelling's code can ever land.
       */
      if (column.field.listRef?.listId) {
        return usableOptions(resolved ?? []);
      }
      return usableOptions(resolved && resolved.length > 0 ? resolved : column.field.values);
    },
    [resolvedLists],
  );

  const { collisions, heldBack, reasons } = useSaveGate({
    rows,
    records,
    dirtyIds: draft.dirtyIds,
    defectsFor,
  });
  heldBackRef.current = heldBack;

  /**
   * Why Save will leave rows behind, or undefined.
   *
   * A reason, never a silently dead button — the rule `BulkToolbar` states for the
   * mint and this copies, because a button that is merely disabled reads as broken
   * while this one is disabled for something the user can fix.
   *
   * One clause per REASON PRESENT, not the single winner of a precedence chain: a
   * draft holding both an incomplete row and a duplicate has to name both, or the
   * user can only discover the second by fixing the first.
   */
  const saveBlockedReason = (() => {
    const clauses: string[] = [];
    if (reasons.incomplete > 0) {
      clauses.push(t('taxonomy.grid.saveBlockedIncomplete', { count: reasons.incomplete }));
    }
    if (reasons.pattern > 0) {
      clauses.push(t('taxonomy.grid.saveBlockedPattern', { count: reasons.pattern }));
    }
    if (reasons.duplicate > 0) {
      clauses.push(t('taxonomy.grid.saveBlockedDuplicate', { count: reasons.duplicate }));
    }
    return clauses.length > 0 ? clauses.join(' ') : undefined;
  })();

  const saveButton = !readOnly ? (
    <Button
      size="default"
      onClick={() => draft.save([], heldBack)}
      /**
       * Also blocked while a mint runs. A mint is one transaction per
       * record, so saving during it advances revisions under the pending
       * code write, which then lands with a stale `expectedRevision` and
       * 409s — a success toast and a conflict banner at once.
       */
      disabled={draft.dirtyIds.size === heldBack.size || draft.saving || generateCodes.isPending}
      title={saveBlockedReason}
    >
      <SaveIcon className="size-4" />
      {/*
        What will actually be SENT, not what is dirty. Counting `dirtyIds` meant
        "Save 4 changes" wrote one record, and nothing on screen accounted for the
        other three.
      */}
      {/* The saved label and the longest "Save N changes" this grid can reach sit invisibly under
          the live one, so the button keeps one width and Export beside it does not move. */}
      <span className="grid tabular-nums">
        <span className="invisible col-start-1 row-start-1" aria-hidden>
          {t('taxonomy.grid.saved')}
        </span>
        <span className="invisible col-start-1 row-start-1" aria-hidden>
          {t('taxonomy.grid.saveDirty', { count: Math.max(rows.length, 2) })}
        </span>
        <span className="col-start-1 row-start-1">
          {draft.isDirty
            ? t('taxonomy.grid.saveDirty', { count: draft.dirtyIds.size - heldBack.size })
            : t('taxonomy.grid.saved')}
        </span>
      </span>
    </Button>
  ) : null;

  return (
    <div className={`flex flex-col gap-sm ${fill ? 'min-h-0 flex-1' : ''}`}>
      <div className="flex items-center justify-between gap-md">
        <div className="flex items-center gap-xs">
          {leading}
          <span className="text-sm text-muted-foreground">
            {t('taxonomy.grid.recordCount', { count: rows.length })}
          </span>
        </div>
        {/*
          Portaled when the page offers a slot, so Save can sit beside Export in
          the page header while the draft it acts on stays here.

          Rendered in place when it does not. That fallback is not politeness: it
          is what lets this component still be mounted and driven on its own,
          which every test in this slice does.

          The alternative was hoisting `useRecordDraft` to the page — but the grid
          reads nine of its members, so that moves one button up and nine props
          back down.
        */}
        {saveSlot ? createPortal(saveButton, saveSlot) : saveButton}
      </div>

      {saveBlockedReason && (
        <span role="status" className="text-xs text-destructive-soft-foreground">
          {saveBlockedReason}
        </span>
      )}

      {draft.conflictedIds.length > 0 && (
        <div
          role="alert"
          className="rounded-md border border-destructive/40 bg-destructive/10 px-md py-sm text-sm"
        >
          {t('taxonomy.grid.conflictBanner', { count: draft.conflictedIds.length })}
        </div>
      )}

      {selectedRows.size > 0 && !readOnly && (
        <BulkToolbar
          count={selectedRows.size}
          onGenerateCodes={
            codeField
              ? () => {
                  /**
                   * Refused here rather than chunked. Every sibling bulk action
                   * chunks to its API bound, but chunking a mint would
                   * manufacture exactly the partial spend the endpoint's
                   * pre-flight exists to prevent — some batches landing, a later
                   * one refused, and no way to undo the ones that went.
                   */
                  if (selectedRows.size > MAX_RECORDS_PER_MINT) {
                    enqueueSnackbar(
                      t('taxonomy.grid.codesTooMany', { max: MAX_RECORDS_PER_MINT }),
                      { variant: 'warning' },
                    );
                    return;
                  }
                  setConfirmCodes(true);
                }
              : undefined
          }
          generatingCodes={generateCodes.isPending}
          /**
           * Minting reads the SAVED records, so unsaved edits would not reach it
           * and a code could be issued from values the user has already changed —
           * permanently, because the code is anchored to the record.
           */
          codesBlockedReason={
            /*
             * Defers to the Save gate when nothing CAN be saved. Telling someone to
             * save first, beside a Save button that is refusing, is an instruction
             * they cannot follow — and the two reasons sat on screen together with
             * nothing linking them.
             */
            draft.isDirty
              ? (draft.dirtyIds.size === heldBack.size && saveBlockedReason) ||
                t('taxonomy.grid.codesNeedSave')
              : undefined
          }
          onDuplicate={() => {
            /**
             * Appends copies of the selected records, values and all.
             *
             * Two steps because appending and writing are separate endpoints:
             * the server creates the rows (so it owns their `sortKey` and the
             * cap), then the copied values land as ordinary unsaved edits. That
             * leaves the copies dirty rather than saved, which is the honest
             * state — the user still has to Save them.
             *
             * A copy of a NAMED record cannot be saved unchanged. The name is
             * assembled from the cells, and the copy holds the same cells, so it
             * assembles the same name and the partial unique index refuses it —
             * correctly, and the same refusal an ordinary pair of identical
             * records gets. Names are not generated until Save, and at Save they
             * are generated IDENTICALLY, so the user is told up front rather than
             * discovering it at the refusal with the copies already created and,
             * after a reload, empty.
             */
            const sources = selectedRecords;
            append.mutate(sources.length, {
              onSuccess: (created) => {
                draft.setCells(
                  created.flatMap((row, i) => {
                    const source = at(sources, i);
                    if (!source) {
                      return [];
                    }
                    return (
                      Object.entries(source.cells)
                        .filter(([, value]) => value !== undefined)
                        /**
                         * A derived cell is NOT copied. A tracking code is
                         * issued to one record for life, so carrying it onto a
                         * copy gives two rows the same permanent join key —
                         * silently merging two things in reporting, and in an
                         * export the column reporting tools are told to join on.
                         * The copy has minted nothing, so it holds nothing.
                         */
                        .filter(([fieldId]) => !derivedFieldIds.has(fieldId))
                        .map(([fieldId, value]) => ({
                          recordId: row.id,
                          fieldId,
                          value: value ?? null,
                        }))
                    );
                  }),
                );
                setSelectedRows(new Set());

                /*
                 * Only the copies that would ACTUALLY collide, which is not the
                 * same as "the source has a name".
                 *
                 * The copy drops derived cells (above), and a computed field is
                 * part of the implicit name — `isFieldInName` excludes only an
                 * opted-out `linked` field. So a source holding a minted code
                 * assembles `NKE_TC00001` while its copy assembles `NKE`, and
                 * they cannot collide at all. Warning there fires on exactly the
                 * schemas tracking codes exist for, which is the crying-wolf this
                 * condition was added to avoid. Assemble the copy's own name and
                 * compare.
                 */
                const collides = sources.filter((source) => {
                  const sourceName = source.assembledName;
                  if (!sourceName) {
                    return false;
                  }
                  const copyCells: TaxonomyRecordCells = Object.fromEntries(
                    Object.entries(source.cells).filter(
                      ([fieldId]) => !derivedFieldIds.has(fieldId),
                    ),
                  );
                  return assemble(copyCells).assembledName === sourceName;
                }).length;
                if (collides > 0) {
                  enqueueSnackbar(t('taxonomy.grid.duplicateNeedsChange', { count: collides }), {
                    variant: 'info',
                  });
                }
              },
            });
          }}
          busy={clear.isPending || remove.isPending}
          onClear={() => setConfirmBulk('clear')}
          onDelete={() => setConfirmBulk('delete')}
        />
      )}

      {rows.length === 0 && (
        /*
         * A real empty state, rather than the prototype's trick of always keeping
         * one blank row so the table is never bare. A phantom row reads as data
         * that exists; this says plainly that none does, and the Add control
         * below is how you get some.
         */
        <EmptyState
          variant="outline-dashed"
          title={t('taxonomy.grid.emptyTitle')}
          description={t('taxonomy.grid.emptyDescription')}
          role="status"
        />
      )}

      <div
        ref={gridRef}
        // The clip keeps the cells (the shaded preview column included) inside the
        // rounded corners: the scroll box below has square ones.
        className={`overflow-hidden rounded-lg border border-border ${
          rows.length === 0 ? 'hidden' : fill ? 'flex min-h-0 flex-col' : ''
        }`}
      >
        <div ref={scrollRef} className={`overflow-auto ${fill ? 'min-h-0' : 'max-h-[70vh]'}`}>
          {/*
            The `<table>` carries the role AND the focus, together.
            `aria-activedescendant` is only valid on a composite widget, and
            `tabIndex` on anything else is a lint rule to silence rather than a
            legitimate stop — so the element that owns arrow-key navigation and
            the clipboard has to be the one holding `role="grid"`. Its rows and
            cells are then the grid's own, not an unrelated table's nested inside
            it.

            `aria-rowcount` and per-row `aria-rowindex` are what make the sheet
            navigable at all while virtualised: only ~50 rows are ever in the
            DOM, so without them a screen reader announces "row 3 of 50" in a
            20 000-record taxonomy.
          */}
          <table
            ref={tableRef}
            role="grid"
            aria-label={t('taxonomy.grid.preview')}
            aria-rowcount={rows.length + 1}
            aria-colcount={totalColumns}
            // Points the screen reader at the cell the arrow keys have moved to.
            // Without it, focus is on the grid itself and no cell is announced.
            aria-activedescendant={sel ? `taxonomy-cell-${sel.fr}-${sel.fc}` : undefined}
            tabIndex={0}
            onFocus={(e) => {
              // Tabbing in lands on the first cell, so the next arrow key moves
              // from somewhere rather than doing nothing. Only when the GRID took
              // focus: `focusin` bubbles, so a row checkbox would otherwise seed
              // a rectangle nobody selected.
              if (!isGridEvent(e) || rows.length === 0 || columns.length === 0) {
                return;
              }
              /*
               * The FUNCTIONAL form, because a click both selects a cell and
               * focuses the grid. Reading `sel` from this render's closure saw the
               * pre-click value — `null` — and seeded 0-0 over the cell the user
               * had just clicked, so a following paste wrote into the first column
               * instead of the clicked one.
               */
              setSel((current) => {
                if (current) {
                  return current;
                }
                const first = at(rows, 0);
                return first
                  ? {
                      ar: 0,
                      ac: 0,
                      fr: 0,
                      fc: 0,
                      anchorRecordId: first.id,
                      focusRecordId: first.id,
                    }
                  : null;
              });
            }}
            onKeyDown={onGridKeyDown}
            onCopy={onGridCopy}
            onPaste={onGridPaste}
            className="table-fixed border-separate border-spacing-0 focus:outline-none [&_tbody_tr:last-child>*]:border-b-0"
            style={{ width: tableWidth }}
          >
            <thead className="sticky top-0 z-20 bg-card">
              <tr aria-rowindex={1}>
                <th className="sticky left-0 z-30 h-16 w-11 border-b border-r border-border bg-card py-2 align-top">
                  <Checkbox
                    checked={allSelected}
                    onCheckedChange={() =>
                      setSelectedRows(allSelected ? new Set() : new Set(rows.map((r) => r.id)))
                    }
                    aria-label={
                      allSelected
                        ? t('taxonomy.grid.clearSelection')
                        : t('taxonomy.grid.selectAllRecords')
                    }
                  />
                </th>
                {/*
                  The second frozen column, offset by the checkbox's own 44px.
                  `bg-card` is not decoration: a sticky cell without an opaque
                  background shows the scrolled content sliding underneath it.
                */}
                <th
                  scope="col"
                  className="sticky left-11 z-30 h-16 w-14 border-b border-r border-border bg-card px-2 py-2 text-right align-top"
                >
                  <span className="sr-only">{t('taxonomy.grid.rowNumber')}</span>
                </th>
                {columns.map((column, ci) => (
                  <th
                    key={column.field.id}
                    scope="col"
                    className="group/column h-16 border-b border-r border-border px-2 py-2 text-left align-top text-xs font-medium"
                    style={{ width: fieldColumnWidth(column.field) }}
                  >
                    {/*
                      The indent moves to the wrapper so it applies to BOTH lines —
                      left on the name alone, a subfield's type tag would hang out
                      from under its own name.
                    */}
                    <span className="flex items-start justify-between gap-1">
                      <span
                        className={`flex min-w-0 flex-col ${column.isSubfield ? 'pl-3 text-muted-foreground' : ''}`}
                      >
                        <span className="line-clamp-2 break-words" title={column.field.name}>
                          {column.field.name}
                          {(column.field.required || requiredByRule.has(column.field.id)) && (
                            <span aria-hidden> *</span>
                          )}
                        </span>
                        {/*
                        Read at render, never hoisted: the map's entries are getters
                        so the copy follows the current language, and a module-scope
                        const would freeze it at import.

                        `aria-hidden` because the type is decoration for a sighted
                        scan — a screen reader announcing "Alpha, Free text" for every
                        column adds noise to a header it already has to read per cell.
                      */}
                        <span aria-hidden className="type-label-xs truncate text-muted-foreground">
                          {TAXONOMY_FIELD_TYPE_LABELS[column.field.type]}
                        </span>
                      </span>
                      {/*
                      Only on a column someone can type into, and never in a
                      read-only grid — a menu whose every action is refused is
                      worse than no menu.

                      A sibling of the name and type stack inside a `justify-between`
                      wrapper, so it cannot change the `<th>`'s height: a ragged
                      height shows as a broken freeze line under `sticky`.
                    */}
                      {!readOnly && column.editable && (
                        <ColumnFillMenu
                          options={columnOptions(column)}
                          selectionCount={selectedRows.size}
                          onFill={(source) => fillColumn(ci, source)}
                        />
                      )}
                    </span>
                  </th>
                ))}
                {previewOutputs.map((output, i) => (
                  <th
                    key={output?.id ?? `implicit-${i}`}
                    scope="col"
                    className="h-16 border-b border-border px-2 py-2 text-left align-top text-xs font-medium"
                  >
                    <span className="block truncate">
                      {output?.name ?? t('taxonomy.grid.preview')}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {padTop > 0 && (
                <tr aria-hidden>
                  <td colSpan={totalColumns} style={{ height: padTop, padding: 0, border: 0 }} />
                </tr>
              )}
              {virtualRows.map((virtual) => {
                const r = virtual.index;
                const row = rows[r];
                const fieldsFor = effective[r] ?? {};
                return (
                  <tr
                    key={row.id}
                    // 1-based, and the header is row 1.
                    aria-rowindex={r + 2}
                    data-highlighted={highlightedIds?.has(row.id) ? 'true' : undefined}
                    className={`group/row transition-[background-color] duration-slow ease-out ${
                      draft.dirtyIds.has(row.id)
                        ? 'bg-primary/5'
                        : highlightedIds?.has(row.id)
                          ? 'bg-primary/10'
                          : ''
                    }`}
                  >
                    <td
                      role="gridcell"
                      className="sticky left-0 z-10 w-11 border-b border-r border-border bg-card text-center"
                    >
                      <Checkbox
                        checked={selectedRows.has(row.id)}
                        onCheckedChange={() => toggleRow(row.id)}
                        aria-label={t('taxonomy.grid.selectRecord', { number: r + 1 })}
                      />
                    </td>
                    {/*
                      A `<th scope="row">`, not a `<td>`: the number NAMES the row, so
                      assistive tech announces it when moving between rows rather than
                      treating it as one more cell of data. It carries no `data-cell`,
                      which is what keeps it out of the sheet — `isCellClipboardEvent`
                      classifies a paste by `closest('td[data-cell]')`.
                    */}
                    <th
                      scope="row"
                      className="sticky left-11 z-10 w-14 border-b border-r border-border bg-card px-2 text-right type-body-sm font-normal tabular-nums text-foreground-alt"
                    >
                      {r + 1}
                    </th>
                    {columns.map((column, c) => {
                      const state = get(fieldsFor, column.field.id);
                      const raw = row.cells[column.field.id];
                      const value = Array.isArray(raw) ? (raw[0] ?? '') : (raw ?? '');
                      return (
                        <RecordCell
                          key={column.field.id}
                          dataCell={`${r}-${c}`}
                          value={state?.hidden ? '' : value}
                          options={state?.options ?? column.field.values}
                          constrained={
                            column.field.type === 'dropdown' ||
                            Boolean(column.field.listRef?.listId)
                          }
                          editable={column.editable && !readOnly && !state?.hidden}
                          bound={Boolean(column.field.listRef?.listId)}
                          selected={inRect(r, c)}
                          isFocus={sel?.fr === r && sel.fc === c}
                          editing={
                            editingCell?.recordId === row.id &&
                            editingCell.fieldId === column.field.id
                          }
                          onEditingChange={(open) => {
                            setEditingCell(
                              open ? { recordId: row.id, fieldId: column.field.id } : null,
                            );
                            // An editor opened on a cell half under the frozen columns or the
                            // header is brought clear, as an arrow-key move would be.
                            if (open) setScrollTo({ r, c });
                          }}
                          placeholder={
                            column.field.id === codeField?.id
                              ? assembleTrackingCode(codeField, fields, row.cells, assembleOptions)
                              : // The schema's declared default, for a cell still empty
                                // DESPITE seeding — one the user cleared, or a type the
                                // server does not seed. Shown, never stored.
                                //
                                // Only on an EDITABLE cell. A computed or linked
                                // column renders through the tracking-code preview
                                // branch, whose label tells a screen reader the text
                                // is an unminted code — which a plain default is not.
                                //
                                // And not on a constrained column: the resting cell
                                // shows the CODE for a stored value, so a default
                                // written as a display value would appear in the
                                // wrong vocabulary beside every filled cell.
                                column.editable &&
                                  !readOnly &&
                                  !state?.hidden &&
                                  column.field.type !== 'dropdown' &&
                                  !column.field.listRef?.listId
                                ? (column.field.defaultValue ?? undefined)
                                : undefined
                          }
                          violation={defectMessage(defectsFor(row).get(column.field.id), column)}
                          onMouseDown={(e) => {
                            dragging.current = true;
                            setSel(
                              e.shiftKey && sel
                                ? { ...sel, fr: r, fc: c, focusRecordId: row.id }
                                : {
                                    ar: r,
                                    ac: c,
                                    fr: r,
                                    fc: c,
                                    anchorRecordId: row.id,
                                    focusRecordId: row.id,
                                  },
                            );
                          }}
                          onMouseEnter={() => {
                            if (dragging.current && sel) {
                              setSel({ ...sel, fr: r, fc: c, focusRecordId: row.id });
                            }
                          }}
                          onCommit={(next) => commitCell(row, column.field.id, next)}
                        />
                      );
                    })}
                    {previewOutputs.map((output, i) => {
                      const assembled = (output ? row.outputs[output.id] : row.assembledName) || '';
                      /*
                       * The FIRST preview column, whatever it is. `previewOutputs`
                       * puts the primary authored output at index 0, or the single
                       * `null` implicit slot when a schema authors none, and
                       * `assemble` takes `assembledName` from index 0 either way —
                       * so index 0 is always the string `uq_taxonomy_record_assembled_name`
                       * keys on.
                       *
                       * Keying on `output` being null instead meant the marker never
                       * rendered at all for a schema with authored outputs: there is
                       * no null entry in that case, so every column was skipped while
                       * the gate still held the row back and the banner still counted
                       * it. The row was withheld with nothing on screen saying which
                       * row it duplicated — the exact complaint this marker answers.
                       */
                      const collidesWith = i === 0 ? collisions.get(row.id) : undefined;
                      return (
                        <td
                          key={output?.id ?? `implicit-${i}`}
                          role="gridcell"
                          /*
                            The lock and the fill say this to sighted users; this is
                            the same thing said to everyone else.

                            Full-strength text on the muted fill, not a muted step.
                            This column is the string people came here to read, and at
                            12px it needs 4.5:1 — `muted-foreground` gives 2.50:1 and
                            `foreground-alt`, the next token down, gives 4.48:1, short
                            by 0.02 with nothing between it and `foreground`. The fill,
                            the lock and the mono face carry "read-only" instead.
                          */
                          aria-readonly
                          className="border-b border-border bg-muted px-2 font-mono text-xs text-foreground"
                        >
                          {/*
                            Muted fill and a lock, because this column is not merely
                            read-only by convention — it is not a `RecordCell` at all:
                            no `data-cell`, no handlers, unreachable by the arrow keys.
                            Nothing about the old styling said so, and people tried to
                            type into it.
                          */}
                          <span className="flex items-center gap-1">
                            <LockIcon className="size-3 shrink-0" aria-hidden />
                            <span className="min-w-0 flex-1 truncate">{assembled || EM_DASH}</span>
                            {/*
                              A collision is a property of the ROW and of its relation
                              to another row, not of any one cell, so it is marked here
                              rather than through `cellDefect`: this column holds the
                              whole-record string that actually clashes, and there is
                              exactly one of it per row. Routing it through the cell
                              predicate would put a triangle on every editable cell for
                              one problem, and would silently make duplicates count as
                              "incomplete" in the export dialog, which gates on the
                              same function.

                              It names the row that keeps the name so the fix is a
                              place to look rather than a hunt.
                            */}
                            {collidesWith !== undefined && (
                              /*
                               * `title` as well as `aria-label`. `aria-label` names the
                               * icon for a screen reader but renders no tooltip, so on
                               * its own the sentence naming the row that holds the name
                               * reached only assistive tech — and telling the two
                               * sentences apart is the whole point of having two. Same
                               * pairing as `RecordCell` and `BulkToolbar`.
                               */
                              <span
                                title={
                                  collidesWith.retained
                                    ? t('taxonomy.grid.duplicatesRowRetained', {
                                        row: collidesWith.row,
                                      })
                                    : t('taxonomy.grid.duplicatesRow', { row: collidesWith.row })
                                }
                                className="flex shrink-0 items-center"
                              >
                                <AlertTriangleIcon
                                  className="size-3.5 shrink-0 text-destructive"
                                  aria-label={
                                    collidesWith.retained
                                      ? t('taxonomy.grid.duplicatesRowRetained', {
                                          row: collidesWith.row,
                                        })
                                      : t('taxonomy.grid.duplicatesRow', { row: collidesWith.row })
                                  }
                                  role="img"
                                />
                              </span>
                            )}
                            {/*
                              Revealed on hover AND on the row holding the selection,
                              but a TAB STOP on only the second.

                              Opacity alone would not have done it: a transparent
                              button is still focusable, so revealing on hover the
                              obvious way puts one stop on every rendered row. The
                              `tabIndex` is what keeps that bounded — mouse users get
                              the affordance where they are pointing, keyboard users
                              get exactly one, on the row they are actually on.

                              Not because the grid is otherwise a single tab stop — it
                              is not, and never was: every row checkbox is already a
                              stop. The point is that this one is bounded.

                              `aria-hidden` goes with it, because opacity does not
                              take an element out of the accessibility tree: without
                              it a screen reader met a "Copy name" button on all
                              eighty rendered rows instead of the one it is on.

                              A keyboard shortcut was the other option and was rejected:
                              Ctrl/Cmd+Shift+C is the browser's own inspect-element
                              binding, so the page never receives it.
                            */}
                            {assembled && (
                              <CopyButton
                                value={assembled}
                                label={t('taxonomy.grid.copyName')}
                                successMessage={t('taxonomy.grid.nameCopied')}
                                tabIndex={sel?.focusRecordId === row.id ? undefined : -1}
                                aria-hidden={sel?.focusRecordId !== row.id}
                                className={
                                  sel?.focusRecordId === row.id
                                    ? 'shrink-0'
                                    : 'shrink-0 opacity-0 group-hover/row:opacity-100'
                                }
                              />
                            )}
                          </span>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
              {padBottom > 0 && (
                <tr aria-hidden>
                  <td colSpan={totalColumns} style={{ height: padBottom, padding: 0, border: 0 }} />
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {confirmCodes && codeField && (
        <FormDialog
          open
          onOpenChange={(next) => setConfirmCodes(next)}
          title={t('taxonomy.grid.codeConfirmTitle')}
        >
          <FormDialog.Body>
            <p className="text-sm text-muted-foreground">
              {t('taxonomy.grid.codeConfirmBody', {
                count: spendingRecords.length,
                remaining: Math.max(
                  0,
                  (settings?.codeCap ?? DEFAULT_TRACKING_CODE_CAP) - codesIssued,
                ),
              })}
            </p>
          </FormDialog.Body>
          {/* Generate is the PRIMARY, matching every other dialog in the module.
              Inverting it to put Cancel under the cursor backfires: people
              dismiss a dialog by clicking the OTHER button, which would have put
              the irreversible action in the dismiss slot. */}
          <FormDialog.Footer
            primary={{ label: t('taxonomy.grid.codeConfirmAction'), onClick: runGenerateCodes }}
            secondary={{ label: t('taxonomy.grid.cancel'), onClick: () => setConfirmCodes(false) }}
          />
        </FormDialog>
      )}

      {/*
        Clear and Delete both stop and ask. They read as one destructive pair by
        design, and they are: both empty typed work irreversibly. The copy is
        what separates them - only Delete costs a tracking code permanently.

        No `isDeleting`: the dialog closes on confirm, and it could not learn when
        the work finished anyway. A select-all delete is CHUNKED into several
        `mutate` calls, and each one detaches the observer from the last, so only
        the final chunk's per-call callback survives to report anything. The
        in-flight state lives on the toolbar buttons instead, which is also what
        stops a second confirm starting before the first settles.
      */}
      {confirmBulk === 'clear' && (
        <DeleteConfirmationModal
          open
          onCancel={() => setConfirmBulk(null)}
          onConfirmDelete={runClear}
          customModalTitle={t('taxonomy.grid.confirmClearTitle', {
            count: selectedRecords.length,
          })}
          warningMessage={t('taxonomy.grid.confirmClearBody')}
          confirmLabel={t('taxonomy.grid.confirmClearAction')}
          data-testid="taxonomy-confirm-clear"
        />
      )}

      {confirmBulk === 'delete' && (
        <DeleteConfirmationModal
          open
          onCancel={() => setConfirmBulk(null)}
          onConfirmDelete={runDelete}
          customModalTitle={t('taxonomy.grid.confirmDeleteTitle', {
            count: selectedRecords.length,
          })}
          warningMessage={t('taxonomy.grid.confirmDeleteBody')}
          confirmLabel={t('taxonomy.grid.confirmDeleteAction')}
          data-testid="taxonomy-confirm-delete"
        />
      )}

      {blocker.state === 'blocked' && (
        <FormDialog
          open
          onOpenChange={(next) => !next && blocker.reset()}
          title={t('taxonomy.grid.unsavedTitle')}
        >
          <FormDialog.Body>
            <p className="text-sm text-muted-foreground">
              {t('taxonomy.grid.unsavedBody', { count: draft.dirtyIds.size })}
            </p>
          </FormDialog.Body>
          <FormDialog.Footer
            primary={{ label: t('taxonomy.grid.stay'), onClick: () => blocker.reset() }}
            secondary={{ label: t('taxonomy.grid.leave'), onClick: () => blocker.proceed() }}
          />
        </FormDialog>
      )}

      {!readOnly && (
        <div className="flex items-center gap-sm">
          <Button
            variant="outline"
            onClick={() => append.mutate(addCount)}
            disabled={append.isPending}
          >
            <PlusIcon className="size-4" />
            {t('taxonomy.grid.addRecords')}
          </Button>
          <Input
            type="number"
            min={1}
            max={500}
            value={addCount}
            onChange={(e) => setAddCount(clamp(Number(e.target.value) || 1, 1, 500))}
            className="h-9 w-20 text-center"
            aria-label={t('taxonomy.grid.addRecords')}
          />
        </div>
      )}
    </div>
  );
}

/**
 * Writes one value down a column.
 *
 * Two actions, because they need different things from the user. "Fill down from
 * first" needs nothing — it is the spreadsheet idiom, and for a free-text column
 * it IS the way to set a value: type it once, then fill. "Set … to" needs a value,
 * so it is offered only where there is a closed vocabulary to pick from.
 *
 * The labels follow the scope. A menu that says "all rows" while touching the five
 * someone selected is worse than either behaviour, so the wording changes with the
 * selection rather than the action doing something the label denies.
 *
 * The trigger stays FOCUSABLE, unlike the cell chevron. That one is inside
 * `role="grid"`'s cell structure, where the pattern wants a single tab stop and
 * arrow keys; a header control is outside it, so removing it from the tab order
 * would make it mouse-only for no benefit. `stopPropagation` keeps the click off
 * the table's own handlers.
 */
function ColumnFillMenu({
  options,
  selectionCount,
  onFill,
}: {
  options: TaxonomyFieldValue[];
  selectionCount: number;
  onFill: (source: { kind: 'value'; value: string } | { kind: 'first' }) => void;
}) {
  const { t } = useTranslation('knowledge');
  const setLabel =
    selectionCount > 0 ? t('taxonomy.grid.fillSetSelectedRows') : t('taxonomy.grid.fillSetAllRows');

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon-mini"
          aria-label={t('taxonomy.grid.fillColumnLabel')}
          onClick={(event) => event.stopPropagation()}
          className="shrink-0 opacity-0 transition-opacity group-hover/column:opacity-100 focus-visible:opacity-100 data-[state=open]:opacity-100"
        >
          <MoreVerticalIcon className="size-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={() => onFill({ kind: 'first' })}>
          {t('taxonomy.grid.fillFromFirst')}
        </DropdownMenuItem>
        {options.length > 0 && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel>{setLabel}</DropdownMenuLabel>
            {options.map((option) => (
              <DropdownMenuItem
                key={option.value}
                onClick={() => onFill({ kind: 'value', value: option.value })}
              >
                {optionLabel(option)}
              </DropdownMenuItem>
            ))}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * One skip reason as a sentence.
 *
 * The reasons are a closed wire vocabulary, and an unrecognised one still has to
 * read as English — a new server code should degrade to "an unexpected problem",
 * not print itself.
 */
function skipReason(
  t: ReturnType<typeof useTranslation<'knowledge'>>['t'],
  reason: string,
): string {
  const known = ['record_incomplete', 'record_not_in_dataset', 'code_mint_failed'] as const;
  return (known as readonly string[]).includes(reason)
    ? t(`taxonomy.grid.codeSkipReason.${reason as (typeof known)[number]}`)
    : t('taxonomy.grid.codeSkipReason.unknown');
}
